import { useEffect, useState, type ReactElement } from "react";

import { api, ApiError } from "../../api/client";
import type { CollabOverview, SubstudyInfo } from "../../api/types";
import { AgeStatsTable } from "../AgeStatsTable";
import { SkeletonRows } from "../Skeleton";
import { ErrorNotice } from "../ErrorNotice";
import { StatsSummary } from "../StatsSummary";
import { StatusCountRows } from "../StatusCountRows";
import { TabPanel } from "../Tabs";

// The collaborator's study-operations home, data first, study documents last:
// substudy-scoped counts, FaceAge stats, demographics, the study's participant
// instructions, and the consent sheet(s) participants sign. Aggregates only —
// no row-level PHI, no trends, no photos. Batch upload was removed 2026-08-12.
export function OverviewTab(): ReactElement {
  const [substudy, setSubstudy] = useState<SubstudyInfo | null>(null);
  const [overview, setOverview] = useState<CollabOverview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [t, o] = await Promise.all([
          api<SubstudyInfo>("GET", "/collab/substudy"),
          api<CollabOverview>("GET", "/collab/overview"),
        ]);
        setSubstudy(t);
        setOverview(o);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "Could not load the study overview.");
      }
    })();
  }, []);

  async function exportAll(): Promise<void> {
    setError(null);
    try {
      // A deidentified console (NeuroAge) exports the two-column
      // study-code + median CSV, never the per-photo PHI export.
      const deidentified = substudy?.deidentified === true;
      const csv = await api<string>(
        "GET",
        deidentified ? "/collab/export/deidentified-results.csv" : "/collab/export/results.csv",
      );
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = deidentified ? "faceage-deidentified-results.csv" : "faceage-all-results.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Export failed.");
    }
  }

  return (
    <TabPanel id="overview" title={substudy ? substudy.name : "Your substudy"}>
      {error ? <ErrorNotice>{error}</ErrorNotice> : null}

      {substudy?.has_access_codes ? (
        <p className="muted">
          Participants join with personal study IDs: they sign up at{" "}
          <code>
            {window.location.origin}/join/{substudy.slug}
          </code>{" "}
          with the ID your team gave them; there is no shared study code or email sign-up.
        </p>
      ) : null}

      <p>
        <button className="btn-primary-glow" onClick={() => void exportAll()}>
          Export all results (CSV)
        </button>{" "}
      </p>

      {overview ? (
        <>
          <h3>Study Counts</h3>
          <table className="data-table breakdown">
            <tbody>
              <StatusCountRows
                label="Participants"
                total={overview.participants_total}
                byStatus={overview.participants_by_status}
              />
              <StatusCountRows
                label="Photos"
                total={overview.photos_total}
                byStatus={overview.photos_by_status}
              />
            </tbody>
          </table>

          <h3>FaceAge summary statistics</h3>
          <AgeStatsTable
            groups={[
              {
                key: "substudy",
                label: substudy?.name ?? "This substudy",
                distribution: overview.age_stats,
              },
            ]}
          />

          <StatsSummary demographics={overview.demographics} n={overview.demographics_n} />

          {overview.instructions.length > 0 ? (
            <>
              <h3>Participant Instructions</h3>
              {overview.instructions.map((block) => (
                <div key={block.from_day}>
                  <h4>
                    {block.title} <span className="muted">(from day {block.from_day})</span>
                  </h4>
                  <p className="muted pre-wrap">
                    {block.markdown}
                  </p>
                </div>
              ))}
            </>
          ) : null}

          <h3>Consent Versions</h3>
          <ul>
            {overview.consent_documents.map((doc) => (
              <li key={doc.version}>
                <a href={doc.url} target="_blank" rel="noopener noreferrer">
                  {doc.version}.pdf
                </a>
              </li>
            ))}
          </ul>
        </>
      ) : !error ? (
        <SkeletonRows rows={4} />
      ) : null}
    </TabPanel>
  );
}
